import { useMemo, useCallback, useState, useEffect, useRef, memo } from "react";
import Box from "@mui/material/Box";
import CardMedia from "../Media/CardMedia";
import SlideTrack from "../Common/SlideTrack";
import { useSlideLock } from "../../hooks/useSlideLock";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import { useInView } from "react-intersection-observer";
import { useTheme } from "@mui/material";
import {
  IMAGE_FORMATS,
  MEDIA_DIMENSIONS,
  VIDEO_FORMATS,
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  EDITOR_ASSETS_LIST_ROOT,
  FEEDBACK_ACTION_DISLIKE,
  FEEDBACK_ACTION_LIKE,
  GALLERY_DRAG_MIME_SELECTED,
} from "../../utils/constants";
import StarIcon from "@mui/icons-material/Star";
import { getExtensionFile, isLikelyImageUrl } from "../../utils/string";
import { formatDimensionsText } from "../../utils/dimensions";
import {
  PNG_ASSET_BACKDROP_SX,
  isPngAssetExtension,
} from "../../utils/assetGalleryUtils";
import { useMediaDimensions } from "../../hooks/useMediaDimensions";
import EditIcon from "@mui/icons-material/Edit";
import EditWithIAIcon from "../../assets/svg/EditIcon";
import DownloadIcon from "@mui/icons-material/Download";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { Share2 } from "lucide-react";
import FormatMenuButton from "./FormatMenuButton";
import _ from "lodash";
import ShareModalMultiple from "./ShareModalMultiple/ShareModalMultiple";
import GalleryAssetNameCaption from "./GalleryAssetNameCaption";
import ContentPlatformStatuses from "./ContentPlatformStatuses";
import ButtonCanvaAction from "../Buttons/ButtonCanvaAction";
import IconButton from "../Buttons/IconButton";
import { useDispatchApp } from "../../lib/redux";
import {
  startGetAwsDownloadUrl,
  startDownloadCarouselZip,
  startDeleteContentFromListAndStorage,
  startReportContent,
} from "../../actions/user";
import {
  BOX_SHADOW_LITTLE,
  PRIMARY_COLOR_TEXT,
  ORANGE_COLOR,
} from "../../utils/colors";
import CropFreeIcon from "@mui/icons-material/CropFree";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFolderAwareDeleteAction } from "../../hooks/useFolderAwareDeleteAction";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ConfirmAlert, SimpleAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import {
  DELETE_CONTENT_CONFIRMATION_TITLE,
  DELETE_CONTENT_CONFIRMATION_DESCRIPTION,
  BUTTON_DELETE_CONTENT,
  BUTTON_DOWNLOAD,
  BUTTON_DELETE,
  BUTTON_EDIT,
  MODAL_BATCH_IMAGE_EDIT_EXPAND,
  MODAL_VIDEO_EDITOR_EDIT_VIDEO,
  POST,
  BAD,
  REGULAR,
  GOOD,
  VERY_GOOD,
  EXCELLENT,
  MODAL_REPORT_CONTENT_SUCCESS,
  GALLERY_CONTENT_APPROVAL_STATUS_REJECTED,
  GALLERY_VIDEO_THUMBNAIL_FALLBACK_TOOLTIP,
} from "../../i18n/keysTranslations";
import Loader from "../Loaders/Loader";
import { isCarouselItem, getCarouselUrls } from "../../utils/carouselUtils";
import { mapContentPlatformStatuses } from "../../utils/contentPlatformStatuses";
import ModalReportContent from "../Modal/ModalReportContent";
import ContentApprovalControls from "../ContentApproval/ContentApprovalControls";
import {
  gallerySecondaryCircleSurfaceSx,
  gallerySecondaryToolbarIconSx,
} from "./galleryIconButtonStyles";
import GalleryLabeledActionButton from "./GalleryLabeledActionButton";

import { LaunchError } from "../../utils/errors";

const sxActionStrip = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  width: "100%",
  flexShrink: 0,
  px: 1,
  py: 0.75,
  containerType: "inline-size",
};

const sxGalleryActionRow = {
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 0,
  width: "100%",
  minWidth: 0,
  overflow: "visible",
};

const sxGalleryActionCluster = {
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: 0.5,
  minWidth: 0,
  overflow: "visible",
};

const sxGalleryActionClusterStart = {
  ...sxGalleryActionCluster,
  flexShrink: 1,
};

const sxGalleryActionClusterEnd = {
  ...sxGalleryActionCluster,
  flexShrink: 0,
};

const sxGalleryActionStripCompact = {
  "@container (max-width: 260px)": {
    gap: 0.25,
    "& .MuiIconButton-root": {
      width: 26,
      height: 26,
      minWidth: 26,
      "& .MuiSvgIcon-root": {
        fontSize: 14,
      },
    },
  },
  "@container (max-width: 220px)": {
    gap: 0.125,
    "& .MuiIconButton-root": {
      width: 24,
      height: 24,
      minWidth: 24,
      "& .MuiSvgIcon-root": {
        fontSize: 13,
      },
    },
  },
  "@container (max-width: 180px)": {
    gap: 0,
    "& .MuiIconButton-root": {
      width: 22,
      height: 22,
      minWidth: 22,
      "& .MuiSvgIcon-root": {
        fontSize: 12,
      },
    },
  },
};

const sxGalleryStripSurface = {
  backgroundColor: "#ffffff",
  backdropFilter: "none",
  WebkitBackdropFilter: "none",
};

const getRatingConfig = (rating) => {
  const numRating = Number(rating);

  if (numRating >= 0 && numRating <= 3) {
    return {
      label: BAD,
      color: "#F44336",
      gradient: null,
      bgColor: "#F44336",
      starColor: "#FFFFFF",
    };
  }

  if (numRating >= 4 && numRating <= 7) {
    return {
      label: REGULAR,
      color: "#FFC107",
      gradient: null,
      bgColor: "#FFC107",
      starColor: "#FFFFFF",
    };
  }

  if (numRating >= 7 && numRating <= 8.5) {
    return {
      label: GOOD,
      color: "#4CAF50",
      gradient: null,
      bgColor: "#4CAF50",
      starColor: "#FFFFFF",
    };
  }

  if (numRating >= 8.5 && numRating <= 9.4) {
    return {
      label: VERY_GOOD,
      color: "#4CAF50",
      gradient: "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
      bgColor: "#4CAF50",
      starColor: "#FFFFFF",
    };
  }

  if (numRating >= 9.5) {
    return {
      label: EXCELLENT,
      color: "#66BB6A",
      gradient: "linear-gradient(135deg, #66BB6A 0%, #81C784 100%)",
      bgColor: "#66BB6A",
      starColor: "#FFFFFF",
      glow: true,
    };
  }

  // Default fallback
  return {
    label: "",
    color: "#FFC107",
    gradient: null,
    bgColor: "#FFC107",
    starColor: "#FFFFFF",
  };
};

// eslint-disable-next-line
const CarouselSlideLoader = () => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#ffffff",
      pointerEvents: "none",
    }}
  >
    <Loader size={32} colorCustom={PRIMARY_COLOR_TEXT} hasMarginTop={false} />
  </Box>
);

// eslint-disable-next-line
const ThreeDCarouselWrapper = ({ isCarousel, stackUrls = [], children }) => {
  if (!isCarousel) return children;

  const visibleStack = (stackUrls || []).filter(Boolean).slice(0, 1);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      {visibleStack
        .slice()
        .reverse()
        .map((url, idx) => {
          // idx = 0 is the farthest card (least visible)
          const depth = idx + 1;
          // Small offset so only the bottom edge peeks out
          const translateY = depth * 6;

          return (
            <Box
              key={`${url}-${idx}`}
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: 3,
                overflow: "hidden",
                transform: `translateY(${translateY}px)`,
                opacity: 0.4 - idx * 0.1,
                border: "1px solid rgba(158, 158, 158, 0.7)", // borde gris sutil
                backgroundImage: `url(${url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                boxShadow: "0 10px 30px rgba(0,0,0,0.28)",
                pointerEvents: "none",
              }}
            />
          );
        })}

      <Box
        sx={{
          position: "relative",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

// eslint-disable-next-line
const GalleryCardContainer = ({
  isSelectable,
  isViewOnly = false,
  isCarousel = false,
  multiple,
  overlayPickerMode = false,
  onSelectImageItem,
  onSelectPreviewImage,
  currentResizing,
  isHighlighted = false,
  draggableForFolders = false,
  onDragStartItem = () => {},
  onDragEndItem = () => {},
  isDragging = false,
  children,
}) => (
  <Box
    draggable={draggableForFolders && isSelectable}
    onDragStart={onDragStartItem}
    onDragEnd={onDragEndItem}
    sx={{
      boxShadow: currentResizing ? "unset" : BOX_SHADOW_LITTLE,
      borderRadius: 3,
      cursor:
        draggableForFolders && isSelectable
          ? "grab"
          : isViewOnly
            ? "default"
            : "pointer",
      position: "relative",
      zIndex: 0,
      display: "flex",
      flexDirection: "column",
      width: "100%",
      overflow: "hidden",
      opacity: isDragging
        ? 0.5
        : isViewOnly || isSelectable || isCarousel
          ? 1
          : 0.25,
      ...(isHighlighted && {
        "@keyframes galleryItemHighlight": {
          "0%": { transform: "scale(1)" },
          "10%": { transform: "scale(1.04)" },
          "20%": { transform: "scale(1)" },
          "35%": { transform: "scale(1.03)" },
          "50%": { transform: "scale(1)" },
          "65%": { transform: "scale(1.025)" },
          "80%": { transform: "scale(1)" },
          "90%": { transform: "scale(1.015)" },
          "100%": { transform: "scale(1)" },
        },
        animation: "galleryItemHighlight 5s ease-in-out",
      }),
    }}
    onClick={(e) => {
      if (isViewOnly) return;
      if (e.target.closest(".gallery-checkbox")) {
        return;
      }
      if (e.target.closest(".gallery-direct-add-button")) {
        return;
      }
      if (multiple || overlayPickerMode) {
        onSelectImageItem(e);
      } else {
        onSelectPreviewImage(e);
      }
    }}
  >
    {children}
  </Box>
);

// eslint-disable-next-line
const CarouselControls = ({
  isCarousel,
  totalImages,
  currentIndex,
  isLocked = false,
  onPrev,
  onNext,
}) => {
  if (!isCarousel || totalImages <= 1) return null;

  const isFirstImage = currentIndex === 0;
  const isLastImage = currentIndex === totalImages - 1;

  return (
    <>
      <IconButton
        onClick={onPrev}
        disabled={isFirstImage || isLocked}
        sx={{
          position: "absolute",
          left: 7,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 3,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          color: "white",
          width: 36,
          height: 36,
          opacity: isFirstImage ? 0.8 : 1,
          "&:hover": {
            backgroundColor: isFirstImage
              ? "rgba(0, 0, 0, 0.6)"
              : "rgba(0, 0, 0, 0.8)",
          },
          "&.Mui-disabled": {
            opacity: 0.8, // Opacidad cuando está deshabilitado
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            cursor: "not-allowed",
          },
          "& .MuiSvgIcon-root": {
            fontSize: 20,
          },
        }}
        type="icon"
      >
        <ChevronLeftIcon sx={{ color: "#fff !important" }} />
      </IconButton>
      <IconButton
        onClick={onNext}
        disabled={isLastImage || isLocked}
        sx={{
          position: "absolute",
          right: 17,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 3,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          color: "white",
          width: 36,
          height: 36,
          opacity: isLastImage ? 0.8 : 1,
          cursor: isLastImage ? "not-allowed" : "pointer",
          "&:hover": {
            backgroundColor: isLastImage
              ? "rgba(0, 0, 0, 0.6)"
              : "rgba(0, 0, 0, 0.8)",
          },
          "&.Mui-disabled": {
            opacity: 0.8, // Opacidad cuando está deshabilitado
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            cursor: "not-allowed",
          },
          "& .MuiSvgIcon-root": {
            fontSize: 20,
          },
        }}
        type="icon"
      >
        <ChevronRightIcon sx={{ color: "#fff !important" }} />
      </IconButton>
    </>
  );
};

// eslint-disable-next-line
const ActionButtonsOverlay = ({
  showActionButtons,
  fromCalendar,
  multiple,
  overlayPickerMode,
  showDownloadButton,
  showDeleteButton,
  deleteActionLabel = "",
  DeleteActionIcon = DeleteIcon,
  isRemoveFromFolder = false,
  showPreviewExpandOnly = false,
  showExpandButton = false,
  showFooterEditButton = false,
  onFooterEdit = () => {},
  showFooterDeleteButton = false,
  onFooterDelete = () => {},
  postLabel,
  isDownloading,
  onDownloadMedia,
  onUploadFromSocial,
  onSelectPreviewImage,
  onDeleteContent,
  showFeedbackButtons,
  liked,
  disliked,
  likeAnimating,
  dislikeAnimating,
  onLikeClick,
  onDislikeClick,
  showReportButton,
  showPostButton = true,
  onOpenReportModal,
  contentApprovalControls = null,
  actionStripMinHeight = 48,
}) => {
  const theme = useTheme();

  if (!showActionButtons) return null;

  const secondaryToolbarSx = gallerySecondaryToolbarIconSx({
    theme,
    iconSvgFontSize: 16,
  });

  const shouldShowDownloadButton =
    showDownloadButton === true ||
    (showDownloadButton !== false &&
      !overlayPickerMode &&
      !showPreviewExpandOnly);

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        ...sxActionStrip,
        minHeight: actionStripMinHeight,
        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
        ...sxGalleryStripSurface,
      }}
    >
      <Box sx={sxGalleryActionRow}>
        <Box
          sx={{
            ...sxGalleryActionClusterStart,
            ...secondaryToolbarSx,
            ...sxGalleryActionStripCompact,
          }}
        >
          {shouldShowDownloadButton ? (
            <IconButton
              onClick={onDownloadMedia}
              type="icon"
              loaderColor={PRIMARY_COLOR_TEXT}
              loading={isDownloading}
              edge={false}
              tooltipText={BUTTON_DOWNLOAD}
            >
              <DownloadIcon
                sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
              />
            </IconButton>
          ) : null}
          {(showPreviewExpandOnly ||
            showExpandButton ||
            multiple ||
            overlayPickerMode) && (
            <IconButton
              onClick={onSelectPreviewImage}
              type="icon"
              edge={false}
              tooltipText={MODAL_BATCH_IMAGE_EDIT_EXPAND}
            >
              <CropFreeIcon
                sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
              />
            </IconButton>
          )}
          {showFooterEditButton && (
            <IconButton
              onClick={onFooterEdit}
              type="icon"
              edge={false}
              tooltipText={BUTTON_EDIT}
            >
              <EditIcon
                sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
              />
            </IconButton>
          )}
          {showFooterDeleteButton && (
            <IconButton
              onClick={onFooterDelete}
              type="icon"
              edge={false}
              tooltipText={BUTTON_DELETE}
            >
              <DeleteActionIcon
                sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
              />
            </IconButton>
          )}
          {!overlayPickerMode && showFeedbackButtons && (
            <>
              <IconButton
                onClick={onLikeClick}
                type="icon"
                sx={{
                  position: "relative",
                }}
                edge={false}
              >
                {likeAnimating && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "50%",
                      backgroundColor: "rgba(107, 138, 255, 0.3)",
                      opacity: 0.3,
                      pointerEvents: "none",
                      "@keyframes pingLike": {
                        "0%": { transform: "scale(1)", opacity: 0.6 },
                        "70%": { transform: "scale(1.8)", opacity: 0 },
                        "100%": { transform: "scale(2)", opacity: 0 },
                      },
                      animation: "pingLike 0.5s ease-out",
                    }}
                  />
                )}
                <ThumbUpIcon
                  sx={{
                    color: liked ? "#6B8AFF" : PRIMARY_COLOR_TEXT,
                    fill: liked ? "#6B8AFF" : PRIMARY_COLOR_TEXT,
                    transform: likeAnimating
                      ? "scale(1.4) rotate(-15deg)"
                      : "scale(1) rotate(0deg)",
                    filter: liked
                      ? "drop-shadow(0 0 6px rgba(107, 138, 255, 0.6))"
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                />
              </IconButton>
              <IconButton
                onClick={onDislikeClick}
                type="icon"
                sx={{
                  position: "relative",
                }}
                edge={false}
              >
                {dislikeAnimating && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: "50%",
                      backgroundColor: "rgba(255, 107, 138, 0.3)",
                      opacity: 0.3,
                      pointerEvents: "none",
                      "@keyframes pingDislike": {
                        "0%": { transform: "scale(1)", opacity: 0.6 },
                        "70%": { transform: "scale(1.8)", opacity: 0 },
                        "100%": { transform: "scale(2)", opacity: 0 },
                      },
                      animation: "pingDislike 0.5s ease-out",
                    }}
                  />
                )}
                <ThumbDownIcon
                  sx={{
                    color: disliked ? "#FF6B8A" : PRIMARY_COLOR_TEXT,
                    fill: disliked ? "#FF6B8A" : PRIMARY_COLOR_TEXT,
                    transform: dislikeAnimating
                      ? "scale(1.4) rotate(15deg)"
                      : "scale(1) rotate(0deg)",
                    filter: disliked
                      ? "drop-shadow(0 0 6px rgba(255, 107, 138, 0.6))"
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                />
              </IconButton>
            </>
          )}
          {showDeleteButton && (
            <Tooltip
              title={isRemoveFromFolder ? deleteActionLabel : ""}
              arrow
              placement="top"
            >
              <span>
                <IconButton onClick={onDeleteContent} type="icon" edge={false}>
                  <DeleteActionIcon
                    sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {contentApprovalControls}
        </Box>
        <Box sx={sxGalleryActionClusterEnd}>
          {!overlayPickerMode && !fromCalendar && showPostButton && (
            <GalleryLabeledActionButton
              variant="primary"
              label={postLabel}
              icon={Share2}
              onClick={onUploadFromSocial}
            />
          )}
          {showReportButton && (
            <Box
              sx={{
                flexShrink: 0,
                ...secondaryToolbarSx,
                ...sxGalleryActionStripCompact,
              }}
            >
              <IconButton
                onClick={onOpenReportModal}
                size="small"
                type="icon"
                edge={false}
              >
                <FlagOutlinedIcon
                  sx={{
                    color: PRIMARY_COLOR_TEXT,
                    fill: PRIMARY_COLOR_TEXT,
                    fontSize: 16,
                  }}
                />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// eslint-disable-next-line
const DimensionsBadge = ({ showDimensionsOverlay, dimensionsText }) => {
  if (!showDimensionsOverlay || !dimensionsText) return null;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        maxWidth: "100%",
      }}
    >
      <Tooltip title={dimensionsText} arrow placement="top">
        <Typography
          variant="caption"
          sx={{
            display: "block",
            px: 1,
            py: 0.35,
            color: "grey.800",
            fontSize: "11px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: "help",
            bgcolor: "transparent",
            border: "none",
            boxShadow: "none",
            textShadow: "none",
          }}
        >
          {dimensionsText}
        </Typography>
      </Tooltip>
    </Box>
  );
};

// eslint-disable-next-line
const VideoThumbnailFallbackAlert = ({ tooltip }) => {
  if (!tooltip) return null;

  return (
    <Tooltip
      title={tooltip}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 280,
            fontSize: 12,
            lineHeight: 1.4,
          },
        },
      }}
    >
      <Box
        component="span"
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "help",
          color: ORANGE_COLOR,
          flexShrink: 0,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 18 }} />
      </Box>
    </Tooltip>
  );
};

// eslint-disable-next-line
const GalleryTopStrip = ({
  isImage,
  isVideo = false,
  showActionButtons,
  showResizeFormatButton,
  showEditButton,
  currentUrl,
  onResizeFormatWithOverlay,
  onEditImageWithCanva,
  onEditImageWithIA,
  onEditVideoWithOverlay = () => {},
  onAnimateImageWithOverlay,
  showAnimate,
  showRatingBadge,
  currentRating,
  t,
  showDimensionsOverlay,
  dimensionsText,
  showVideoThumbnailFallback = false,
  videoThumbnailFallbackTooltip,
  isSelectable,
  isViewOnly = false,
  multiple,
  overlayPickerMode = false,
  isSelected,
  onSelectImageItem,
}) => {
  const theme = useTheme();
  const resolvedShowEditButton =
    showEditButton ?? (showActionButtons || showResizeFormatButton);
  const hasEdit = isImage && resolvedShowEditButton;
  const hasVideoEdit = isVideo && resolvedShowEditButton;
  const showCheckbox =
    isSelectable && (multiple || overlayPickerMode) && !isViewOnly;
  const showDims = Boolean(showDimensionsOverlay && dimensionsText);
  const hasCenter = showRatingBadge || showDims || showVideoThumbnailFallback;

  if (!hasEdit && !hasVideoEdit && !hasCenter && !showCheckbox) {
    return null;
  }

  const editToolbarSecondarySx = gallerySecondaryToolbarIconSx({
    theme,
    iconSvgFontSize: 18,
  });

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        ...sxActionStrip,
        py: 1,
        px: 1,
        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        ...sxGalleryStripSurface,
        minHeight: "auto",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, auto) minmax(0, 1fr) minmax(0, auto)",
          alignItems: "center",
          gap: 1,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            minHeight: hasEdit || hasVideoEdit ? 40 : 0,
            minWidth: 32,
          }}
        >
          {hasVideoEdit ? (
            <Box
              sx={{
                pointerEvents: "auto",
                ...editToolbarSecondarySx,
              }}
            >
              <IconButton
                onClick={onEditVideoWithOverlay}
                type="icon"
                edge={false}
                tooltipText={MODAL_VIDEO_EDITOR_EDIT_VIDEO}
              >
                <EditWithIAIcon
                  color={PRIMARY_COLOR_TEXT}
                  width="24px"
                  height="24px"
                />
              </IconButton>
            </Box>
          ) : hasEdit ? (
            <Box
              sx={{
                pointerEvents: "auto",
                ...editToolbarSecondarySx,
              }}
            >
              {showResizeFormatButton ? (
                <FormatMenuButton
                  url={currentUrl}
                  onResizeFormat={onResizeFormatWithOverlay}
                  onEditWithCanva={onEditImageWithCanva}
                  onEditWithIA={onEditImageWithIA}
                  onAnimateImage={onAnimateImageWithOverlay}
                  showAnimate={showAnimate}
                  hideResizeOptions={false}
                  iconColor="currentColor"
                />
              ) : (
                <ButtonCanvaAction
                  icon={
                    <EditIcon
                      sx={{
                        color: PRIMARY_COLOR_TEXT,
                        fill: PRIMARY_COLOR_TEXT,
                      }}
                    />
                  }
                  onClick={onEditImageWithCanva}
                  type="icon"
                  loaderColor={PRIMARY_COLOR_TEXT}
                />
              )}
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            minWidth: 0,
            py: hasCenter ? 0.25 : 0,
            mr: 1,
          }}
        >
          <RatingBadge
            showRating={showRatingBadge}
            currentRating={currentRating}
            t={t}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <DimensionsBadge
              showDimensionsOverlay={showDimensionsOverlay}
              dimensionsText={dimensionsText}
            />
            {showVideoThumbnailFallback ? (
              <VideoThumbnailFallbackAlert
                tooltip={videoThumbnailFallbackTooltip}
              />
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            minHeight: showCheckbox ? 40 : 0,
          }}
        >
          <SelectionCheckbox
            isSelectable={isSelectable}
            multiple={multiple}
            overlayPickerMode={overlayPickerMode}
            isSelected={isSelected}
            onSelect={onSelectImageItem}
          />
        </Box>
      </Box>
    </Box>
  );
};

// eslint-disable-next-line
const RatingBadge = ({ showRating, currentRating, t }) => {
  if (!showRating || typeof currentRating !== "number") return null;

  const ratingConfig = getRatingConfig(currentRating);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        flexWrap: "wrap",
        background: ratingConfig.gradient || ratingConfig.bgColor,
        backgroundImage: ratingConfig.gradient || undefined,
        borderRadius: "16px",
        px: 1,
        py: 0.3,
        boxShadow: ratingConfig.glow
          ? `0 0 8px ${ratingConfig.bgColor}40, 0 0 16px ${ratingConfig.bgColor}20`
          : undefined,
      }}
    >
      <StarIcon
        sx={{
          color: `${ratingConfig.starColor} !important`,
          fontSize: 14,
          mt: -0.25,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: `${ratingConfig.starColor} !important`,
          fontWeight: 600,
          fontSize: "0.65rem",
        }}
      >
        {Number(currentRating).toFixed(1)}
      </Typography>
      {ratingConfig.label && (
        <Typography
          variant="caption"
          sx={{
            color: `${ratingConfig.starColor} !important`,
            fontWeight: 600,
            fontSize: "0.65rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {t(ratingConfig.label)}
        </Typography>
      )}
    </Box>
  );
};

// eslint-disable-next-line
const SelectionCheckbox = ({
  isSelectable,
  multiple,
  overlayPickerMode = false,
  isSelected,
  onSelect,
}) => {
  const theme = useTheme();

  if (!isSelectable || !(multiple || overlayPickerMode)) return null;

  return (
    <Box
      className="gallery-checkbox"
      onClick={(e) => {
        e.stopPropagation();
        onSelect(e);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: 34,
        height: 34,
        borderRadius: "50%",
        cursor: "pointer",
        transition: "all 0.2s ease",
        ...gallerySecondaryCircleSurfaceSx(theme),
        "&:hover": {
          backgroundColor: `${theme.palette.button.secondary.hover.main} !important`,
        },
      }}
    >
      {isSelected ? (
        <CheckCircleIcon
          sx={{
            color: "#4caf50 !important",
            fontSize: "26px",
            "& path": {
              stroke: "#ffffff",
              strokeWidth: "3px",
              paintOrder: "stroke fill",
            },
          }}
        />
      ) : (
        <RadioButtonUncheckedIcon
          sx={{
            color: `${PRIMARY_COLOR_TEXT} !important`,
            fontSize: "28px",
          }}
        />
      )}
    </Box>
  );
};

// eslint-disable-next-line
const VideoMuteToggle = ({ isMuted, onToggleMute }) => (
  <Box
    className="gallery-video-mute-toggle"
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0,
      transition: "opacity 0.2s ease",
      pointerEvents: "none",
    }}
  >
    <IconButton
      onClick={onToggleMute}
      aria-pressed={!isMuted}
      type="icon"
      sx={{
        pointerEvents: "none",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        color: "white",
        width: 50,
        height: 50,
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        },
        "& .MuiSvgIcon-root": {
          fontSize: 30,
        },
      }}
    >
      {isMuted ? (
        <VolumeOffIcon sx={{ color: "#fff !important" }} />
      ) : (
        <VolumeUpIcon sx={{ color: "#fff !important" }} />
      )}
    </IconButton>
  </Box>
);

// eslint-disable-next-line
const ResizingPlaceholder = ({ currentResizing }) => {
  if (!currentResizing) return null;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: 3,
        backgroundColor: "#9E9E9E",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "@keyframes pulse": {
          "0%": { opacity: 0.6, transform: "scale(1)" },
          "50%": { opacity: 0.9, transform: "scale(1.02)" },
          "100%": { opacity: 0.6, transform: "scale(1)" },
        },
        animation: "pulse 1.2s ease-in-out infinite",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Loader size={32} colorCustom="white" hasMarginTop={false} />
    </Box>
  );
};

// eslint-disable-next-line
const GalleryItem = ({
  propsMedia = {},
  item,
  index,
  isSelected,
  onSelectItem = () => {},
  onCallbackResizeFormat = () => {},
  onAnimateImage = () => {},
  onFailedLoadUrl = () => {},
  onSelectPreview = () => {},
  onSelectCarousel = undefined,
  onEditWithCanva = () => {},
  onEditWithIA = () => {},
  onEditWithVideo = () => {},
  onDeleteItem = () => {},
  onRemoveFromFolder = null,
  onDownloadItems = () => {},
  listRoot = "contentList",
  multiple,
  from,
  acceptedFormats = [],
  minDimensions = {},
  showDimensionsOverlay = true,
  showActionButtons = true,
  showResizeFormatButton = false,
  showEditButton,
  showDeleteButton = true,
  showPreviewExpandOnly = false,
  showExpandButton = false,
  showFooterEditButton = false,
  onFooterEdit = () => {},
  showFooterDeleteButton = false,
  onFooterDelete = () => {},
  overlayPickerMode = false,
  showDownloadButton,
  showRating = false,
  itemDimensions,
  scheduledDate = null,
  fromCalendar = false,
  showFeedbackButtons = false,
  onFeedback,
  showReportButton = false,
  showPostButton = true,
  onRefreshCalendar,
  onScheduledShareSuccess,
  onShareSuccess,
  showContentApproval = false,
  getContentApprovalData = () => null,
  enablingContentApprovalId = null,
  showContentApprovalEnableButton = true,
  onEnableContentApproval = () => {},
  onOpenContentApprovalReview = () => {},
  showShimmer = false,
  unmutedVideoKey = null,
  onRequestVideoUnmute = undefined,
  onRequestVideoMute = undefined,
  isHighlighted = false,
  draggableForFolders = false,
  itemSelectionId = null,
  selectedItemsCount = 0,
  showAssetName = false,
  onRenameAsset = () => {},
  actionStripMinHeight = 48,
  releaseWhenHidden = false,
}) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [hasSuccessfullyReportedContent, setHasSuccessfullyReportedContent] =
    useState(false);
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  const [readyCarouselUrls, setReadyCarouselUrls] = useState({});
  const platformStatuses = useMemo(
    () =>
      mapContentPlatformStatuses({
        publishedSources: item?.publishedSources,
      }),
    [item?.publishedSources]
  );

  useEffect(() => {
    setHasSuccessfullyReportedContent(false);
  }, [item?.id, item?.url, item?.stableKey]);

  // Carousel state: current index
  const isCarousel = isCarouselItem(item);
  // Unmount the media (and free its memory) while the card is far off screen.
  // The observer sits on the media container, which never unmounts, so it
  // keeps tracking the card while the media is swapped for a skeleton.
  const { ref: mediaInViewRef, inView: isMediaInView } = useInView({
    rootMargin: "800px 0px",
    skip: !releaseWhenHidden,
    initialInView: true,
  });
  const showMedia = !releaseWhenHidden || isMediaInView;
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const { isSlideLocked, lockSlide } = useSlideLock();

  useEffect(() => {
    setCurrentCarouselIndex(0);
    setReadyCarouselUrls({});
  }, [item?.id, item?.stableKey, item?.url]);

  const markCarouselUrlReady = useCallback((url) => {
    if (!url) return;
    setReadyCarouselUrls((prev) =>
      prev[url] ? prev : { ...prev, [url]: true }
    );
  }, []);

  const carouselSlideUrls = useMemo(
    () =>
      isCarousel
        ? (item?.carouselImages || []).map((img) => img?.url || "")
        : [],
    [isCarousel, item?.carouselImages]
  );

  const carouselBaseUrl = useMemo(() => {
    if (!isCarousel) return null;
    return item?.carouselImages?.[0]?.url || item?.url || null;
  }, [isCarousel, item?.carouselImages, item?.url]);
  const currentCarouselImage = isCarousel
    ? item.carouselImages[currentCarouselIndex]
    : null;
  const currentUrl = isCarousel ? currentCarouselImage?.url : item.url;
  const currentRating = isCarousel ? currentCarouselImage?.rating : item.rating;
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [likeAnimating, setLikeAnimating] = useState(false);
  const [dislikeAnimating, setDislikeAnimating] = useState(false);
  const [localIsVideoMuted, setLocalIsVideoMuted] = useState(true);
  const isControlledVideoMute = Boolean(onRequestVideoUnmute);
  const prevVideoMuteKeyRef = useRef(null);
  const feedbackUrls = useMemo(() => {
    if (!currentUrl) return [];
    if (isCarousel && Array.isArray(item?.carouselImages)) {
      return item.carouselImages.map((img) => img?.url).filter((url) => !!url);
    }
    return [currentUrl];
  }, [currentUrl, isCarousel, item?.carouselImages]);

  const extension = useMemo(() => getExtensionFile(currentUrl), [currentUrl]);
  const showPngAssetBackdrop =
    listRoot === EDITOR_ASSETS_LIST_ROOT &&
    isPngAssetExtension({ extension }) &&
    !isCarousel;

  const isVideo = useMemo(() => {
    if (!currentUrl) return false;
    if (currentUrl.startsWith("data:video/")) return true;
    return VIDEO_FORMATS.includes(extension);
  }, [currentUrl, extension]);

  const videoMuteKey = useMemo(() => {
    const itemKey =
      item?.stableKey || item?.id || item?.url || `index-${index}`;
    return `${itemKey}::${currentUrl}`;
  }, [item?.stableKey, item?.id, item?.url, index, currentUrl]);

  const isVideoMuted = isControlledVideoMute
    ? unmutedVideoKey !== videoMuteKey
    : localIsVideoMuted;

  useEffect(() => {
    const prevVideoMuteKey = prevVideoMuteKeyRef.current;

    if (prevVideoMuteKey !== null && prevVideoMuteKey !== videoMuteKey) {
      if (isControlledVideoMute) {
        if (unmutedVideoKey === prevVideoMuteKey) {
          onRequestVideoMute?.();
        }
      } else {
        setLocalIsVideoMuted(true);
      }
    }

    prevVideoMuteKeyRef.current = videoMuteKey;
    // Only reset mute when the visible video URL changes (e.g. carousel slide)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUrl]);

  // Detect image type by extension / extensionless CDN URLs without loading
  const isImageByExtension = useMemo(
    () => isLikelyImageUrl(currentUrl),
    [currentUrl]
  );

  // Dimensions priority: backend -> cache -> hook as fallback
  const hasBackendDimensions = !!(item?.width && item?.height);

  const cachedDimensions = itemDimensions?.get(
    isCarousel ? carouselBaseUrl || currentUrl : currentUrl
  );

  const dimensionsSourceUrl = isCarousel ? carouselBaseUrl : currentUrl;

  const shouldLoadDimensions =
    !cachedDimensions && !hasBackendDimensions && !!dimensionsSourceUrl;

  const {
    dimensions: individualDimensions,
    aspectRatio: individualAspectRatio,
    isImage: individualIsImage,
    loading: individualLoading,
  } = useMediaDimensions(shouldLoadDimensions ? dimensionsSourceUrl : null);

  // Priority: backend -> cache -> hook
  const dimensions = useMemo(() => {
    if (hasBackendDimensions) {
      return { width: item.width, height: item.height };
    }
    if (cachedDimensions) return cachedDimensions;
    return individualDimensions;
  }, [
    hasBackendDimensions,
    item?.width,
    item?.height,
    cachedDimensions,
    individualDimensions,
  ]);

  const aspectRatio = useMemo(() => {
    if (hasBackendDimensions && item.width && item.height) {
      return item.width / item.height;
    }
    if (cachedDimensions?.width && cachedDimensions?.height) {
      return cachedDimensions.width / cachedDimensions.height;
    }
    if (individualAspectRatio) return individualAspectRatio;

    return 4 / 5;
  }, [
    hasBackendDimensions,
    item?.width,
    item?.height,
    cachedDimensions,
    individualAspectRatio,
  ]);

  const isImage = hasBackendDimensions
    ? isImageByExtension
    : cachedDimensions
      ? isImageByExtension
      : individualIsImage;

  const loading =
    hasBackendDimensions || cachedDimensions ? false : individualLoading;

  const isCurrentCarouselSlideLoading =
    isCarousel && !!currentUrl && !readyCarouselUrls[currentUrl];

  const hasImageFormatAccepted = useMemo(
    () => (acceptedFormats || []).some((fmt) => IMAGE_FORMATS.includes(fmt)),
    [acceptedFormats]
  );

  const hasVideoFormatAccepted = useMemo(
    () => (acceptedFormats || []).some((fmt) => VIDEO_FORMATS.includes(fmt)),
    [acceptedFormats]
  );

  const isCarouselFormatAccepted = useMemo(
    () => (acceptedFormats || []).includes(ADS_CAMPAIGN_CAROUSEL_FORMAT),
    [acceptedFormats]
  );

  const isAcceptedFormat = useMemo(() => {
    if (_.isEmpty(acceptedFormats)) return true;

    if (from === "posts") {
      return isImage ? hasImageFormatAccepted : hasVideoFormatAccepted;
    }

    if (acceptedFormats.includes(extension)) return true;

    const isKnownExtension =
      IMAGE_FORMATS.includes(extension) || VIDEO_FORMATS.includes(extension);
    if (!isKnownExtension) {
      return isImage ? hasImageFormatAccepted : hasVideoFormatAccepted;
    }

    return false;
    //eslint-disable-next-line
  }, [
    acceptedFormats,
    extension,
    isImage,
    hasImageFormatAccepted,
    hasVideoFormatAccepted,
    from,
    item?.from,
    currentUrl,
  ]);

  const meetsMinDimensions = useMemo(() => {
    if (!minDimensions?.width || !minDimensions?.height) return true;
    if (!dimensions?.width || !dimensions?.height) return false;
    return (
      dimensions.width >= minDimensions.width &&
      dimensions.height >= minDimensions.height
    );
  }, [
    dimensions?.width,
    dimensions?.height,
    minDimensions?.width,
    minDimensions?.height,
  ]);

  const isViewOnly = false;

  const isSelectable = useMemo(() => {
    if (isCarousel) {
      return isCarouselFormatAccepted && meetsMinDimensions;
    }

    return isAcceptedFormat && !loading && meetsMinDimensions;
  }, [
    isCarousel,
    isAcceptedFormat,
    isCarouselFormatAccepted,
    loading,
    meetsMinDimensions,
  ]);

  const onDragStartItem = useCallback(
    (event) => {
      if (!draggableForFolders || !itemSelectionId || !isSelectable) return;

      event.dataTransfer.setData("text/plain", itemSelectionId);
      if (isSelected && selectedItemsCount > 1) {
        event.dataTransfer.setData(GALLERY_DRAG_MIME_SELECTED, "1");
      }
      event.dataTransfer.effectAllowed = "move";
      setIsDraggingItem(true);
    },
    [
      draggableForFolders,
      isSelectable,
      isSelected,
      itemSelectionId,
      selectedItemsCount,
    ]
  );

  const onDragEndItem = useCallback(() => {
    setIsDraggingItem(false);
  }, []);

  const dimensionsText = useMemo(
    () => formatDimensionsText(dimensions),
    [dimensions]
  );

  const handleCarouselNext = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (!isCarousel) return;
      const maxIndex = item.carouselImages.length - 1;
      if (currentCarouselIndex >= maxIndex || !lockSlide()) return;
      setCurrentCarouselIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
    },
    [isCarousel, item?.carouselImages?.length, currentCarouselIndex, lockSlide]
  );

  const handleCarouselPrev = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (!isCarousel) return;
      if (currentCarouselIndex <= 0 || !lockSlide()) return;
      setCurrentCarouselIndex((prev) => (prev > 0 ? prev - 1 : prev));
    },
    [isCarousel, currentCarouselIndex, lockSlide]
  );

  const onSelectImageItem = useCallback(
    (e) => {
      if (e) e?.stopPropagation();
      if (!isSelectable) return;
      onSelectItem(index);
    },
    [isSelectable, onSelectItem, index]
  );

  const onSelectPreviewImage = useCallback(
    (e) => {
      if (e) e?.stopPropagation();

      if (!isSelectable && !isViewOnly) return;

      if (isCarousel && onSelectCarousel && !multiple) {
        onSelectCarousel(index);
        return;
      }

      if (isCarousel) {
        onSelectPreview({
          index,
          carouselIndex: currentCarouselIndex,
          isCarousel: true,
          isFromCarousel: true,
        });
      } else {
        onSelectPreview(index);
      }
    },
    [
      isSelectable,
      isViewOnly,
      onSelectPreview,
      index,
      isCarousel,
      onSelectCarousel,
      multiple,
      currentCarouselIndex,
    ]
  );

  const onFailedLoad = useCallback(() => {
    if (isCarousel && currentUrl) {
      markCarouselUrlReady(currentUrl);
    }
    onFailedLoadUrl(index);
  }, [currentUrl, isCarousel, onFailedLoadUrl, index, markCarouselUrlReady]);

  const onEditImageWithCanva = useCallback(
    (e) => {
      if (e) e?.stopPropagation();

      if (onEditWithCanva) {
        onEditWithCanva({
          url: currentUrl,
          dimensions: dimensions,
          index: index,
          ...(isCarousel && {
            carouselIndex: currentCarouselIndex,
            isCarousel: true,
            itemId: item.id,
          }),
        });
      }
    },
    [
      currentUrl,
      dimensions,
      onEditWithCanva,
      index,
      isCarousel,
      currentCarouselIndex,
      item.id,
    ]
  );

  const onEditImageWithIA = useCallback(
    (e) => {
      if (e) e?.stopPropagation();

      if (onEditWithIA) {
        onEditWithIA({
          url: currentUrl,
          dimensions: dimensions,
          index: index,
          itemId: item.id,
          ...(isCarousel && {
            carouselIndex: currentCarouselIndex,
            isCarousel: true,
          }),
        });
      }
    },
    [
      currentUrl,
      dimensions,
      onEditWithIA,
      index,
      isCarousel,
      currentCarouselIndex,
      item.id,
    ]
  );

  const onEditVideoWithOverlay = useCallback(
    (e) => {
      if (e) e?.stopPropagation();

      if (onEditWithVideo) {
        onEditWithVideo({
          url: currentUrl,
          index,
          itemId: item.id,
          name: item.name,
          sourceUrl: item.sourceUrl || item.originalVideoUrl || "",
          subtitles: item.subtitles || null,
        });
      }
    },
    [
      currentUrl,
      index,
      item.id,
      item.name,
      item.originalVideoUrl,
      item.sourceUrl,
      item.subtitles,
      onEditWithVideo,
    ]
  );

  const onFooterEditClick = useCallback(
    (e) => {
      e?.stopPropagation();
      onFooterEdit({
        item,
        index,
        url: currentUrl,
        ...(isCarousel && {
          carouselIndex: currentCarouselIndex,
          isCarousel: true,
        }),
      });
    },
    [currentCarouselIndex, currentUrl, index, isCarousel, item, onFooterEdit]
  );

  const onFooterDeleteClick = useCallback(
    (e) => {
      e?.stopPropagation();
      onFooterDelete({ item, index });
    },
    [index, item, onFooterDelete]
  );

  const onDownloadMedia = useCallback(
    async (e) => {
      if (e) e?.stopPropagation();
      try {
        setIsDownloading(true);
        if (isCarousel) {
          const urls = getCarouselUrls(item);
          await dispatch(startDownloadCarouselZip({ urls }));
          onDownloadItems(urls);
        } else {
          await dispatch(startGetAwsDownloadUrl(currentUrl));
          onDownloadItems([currentUrl]);
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "GalleryItem",
            capture: "exception",
          })
        );
        console.error("Error al descargar el archivo:", error);
      }
      setIsDownloading(false);
    },
    [dispatch, currentUrl, isCarousel, item, onDownloadItems]
  );

  const onUploadFromSocial = useCallback((e) => {
    e.stopPropagation();
    setIsShareModalOpen(true);
  }, []);

  const onOpenReportModal = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsReportModalOpen(true);
  }, []);

  const onCloseReportModal = useCallback(() => {
    setIsReportModalOpen(false);
  }, []);

  const onSubmitReport = useCallback(
    async ({ reason }) => {
      if (!feedbackUrls.length) {
        return { ok: false };
      }

      setIsSubmittingReport(true);
      const response = await dispatch(
        startReportContent({
          reason,
          urls: feedbackUrls,
        })
      );
      setIsSubmittingReport(false);

      if (response?.ok) {
        setHasSuccessfullyReportedContent(true);
        await SimpleAlert({
          title: t(MODAL_REPORT_CONTENT_SUCCESS),
          icon: "success",
        });
        setIsReportModalOpen(false);
      }

      return response;
    },
    [dispatch, feedbackUrls, t]
  );

  const onDeleteContent = useCallback(
    async (e) => {
      e?.stopPropagation();

      const result = await ConfirmAlert({
        title: t(DELETE_CONTENT_CONFIRMATION_TITLE),
        text: t(DELETE_CONTENT_CONFIRMATION_DESCRIPTION),
        confirmButtonText: t(BUTTON_DELETE_CONTENT),
      });

      if (!result.isConfirmed) return;

      try {
        const contentID = item.id;

        const response = await dispatch(
          startDeleteContentFromListAndStorage({
            contentID,
            listRoot,
          })
        );

        if (response) {
          onDeleteItem(index);
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "GalleryItem",
            capture: "exception",
          })
        );
        console.error("Error deleting content:", error);
      }
    },
    [dispatch, item.id, index, t, onDeleteItem, listRoot]
  );

  const onRemoveContentFromFolder = useCallback(
    (e) => {
      e?.stopPropagation();
      onRemoveFromFolder?.(item);
    },
    [item, onRemoveFromFolder]
  );

  const {
    label: deleteActionLabel,
    Icon: DeleteActionIcon,
    onAction: onDeleteOrRemoveContent,
    isRemoveFromFolder,
  } = useFolderAwareDeleteAction({
    activeFolderID: onRemoveFromFolder ? true : null,
    deleteLabel: "",
    deleteIcon: DeleteIcon,
    onDelete: onDeleteContent,
    onRemoveFromFolder: onRemoveContentFromFolder,
  });

  const onResizeFormatWithOverlay = useCallback(
    async ({ format, formats, width, height, onCreditsConfirmed }) => {
      const resolvedFormats = Array.isArray(formats)
        ? formats
        : format
          ? [format]
          : [];

      const carouselUrls =
        isCarousel && item?.carouselImages
          ? item.carouselImages.map((img) => img.url || img).filter(Boolean)
          : undefined;

      return await onCallbackResizeFormat({
        index,
        formats: resolvedFormats,
        format: resolvedFormats[0],
        width,
        height,
        isCarousel: isCarousel && carouselUrls && carouselUrls.length > 1,
        carouselUrls,
        onCreditsConfirmed,
      });
    },
    [onCallbackResizeFormat, index, isCarousel, item?.carouselImages]
  );

  const onAnimateImageWithOverlay = useCallback(async () => {
    await onAnimateImage({ index });
  }, [onAnimateImage, index]);

  const onLike = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (!feedbackUrls.length) return;

      const isAlreadyLiked = liked;
      if (isAlreadyLiked) {
        onFeedback?.("remove", feedbackUrls);
        setLiked(false);
        return;
      }

      setLiked(true);
      if (disliked) setDisliked(false);

      setLikeAnimating(true);
      setTimeout(() => {
        setLikeAnimating(false);
      }, 500);

      onFeedback?.(FEEDBACK_ACTION_LIKE, feedbackUrls);
    },
    [feedbackUrls, liked, disliked, onFeedback]
  );

  const onDislike = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (!feedbackUrls.length) return;

      const isAlreadyDisliked = disliked;

      // Si ya estaba en estado "disliked", el segundo click ejecuta una acción de "remove"
      if (isAlreadyDisliked) {
        onFeedback?.("remove", feedbackUrls);
        setDisliked(false);
        return;
      }

      // Primer dislike: marcamos como "disliked" y enviamos feedback normal
      setDisliked(true);
      if (liked) setLiked(false);

      setDislikeAnimating(true);
      setTimeout(() => {
        setDislikeAnimating(false);
      }, 500);

      onFeedback?.(FEEDBACK_ACTION_DISLIKE, feedbackUrls);
    },
    [feedbackUrls, liked, disliked, onFeedback]
  );

  const onToggleVideoMute = useCallback(
    (e) => {
      if (e) e.stopPropagation();

      if (isControlledVideoMute) {
        if (isVideoMuted) {
          onRequestVideoUnmute({ videoMuteKey });
        } else {
          onRequestVideoMute?.();
        }
        return;
      }

      setLocalIsVideoMuted((prev) => !prev);
    },
    [
      isControlledVideoMute,
      isVideoMuted,
      onRequestVideoUnmute,
      onRequestVideoMute,
      videoMuteKey,
    ]
  );

  const renderContent = () => {
    const getAspectRatioByFormat = (format) => {
      if (format === MEDIA_DIMENSIONS.custom) {
        const { resizingWidth, resizingHeight } = item;
        if (resizingWidth && resizingHeight) {
          return `${resizingWidth} / ${resizingHeight}`;
        }
      }
      if (format === MEDIA_DIMENSIONS.square) return "1 / 1";
      if (format === MEDIA_DIMENSIONS.horizontal) return "16 / 9";
      if (format === MEDIA_DIMENSIONS.feed) return "4 / 5";
      return "9 / 16";
    };

    const currentResizing = item.resizingFormat;
    const containerAspectRatio =
      currentResizing && currentResizing !== "animate"
        ? getAspectRatioByFormat(currentResizing)
        : aspectRatio;

    const showRatingBadge = showRating && typeof currentRating === "number";
    const contentApprovalData =
      showContentApproval && !overlayPickerMode && !fromCalendar
        ? getContentApprovalData(item)
        : null;
    const contentApprovalControlsProps = contentApprovalData
      ? {
          approvalData: contentApprovalData,
          isEnabling: enablingContentApprovalId === item?.id,
          showEnableButton: showContentApprovalEnableButton,
          onEnableApproval: () => onEnableContentApproval(index, item),
          onOpenReview: () => onOpenContentApprovalReview(index, item),
        }
      : null;

    return (
      <GalleryCardContainer
        isSelectable={isSelectable}
        isViewOnly={isViewOnly}
        isCarousel={isCarousel}
        multiple={multiple}
        overlayPickerMode={overlayPickerMode}
        currentResizing={currentResizing}
        isHighlighted={isHighlighted}
        draggableForFolders={draggableForFolders}
        onDragStartItem={onDragStartItem}
        onDragEndItem={onDragEndItem}
        isDragging={isDraggingItem}
        onSelectImageItem={onSelectImageItem}
        onSelectPreviewImage={onSelectPreviewImage}
      >
        {currentResizing ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: containerAspectRatio,
              flexShrink: 0,
            }}
          >
            <ResizingPlaceholder currentResizing={currentResizing} />
          </Box>
        ) : (
          <>
            <GalleryTopStrip
              isImage={isImage}
              isVideo={isVideo}
              showActionButtons={showActionButtons}
              showResizeFormatButton={showResizeFormatButton}
              showEditButton={showEditButton}
              currentUrl={currentUrl}
              onResizeFormatWithOverlay={onResizeFormatWithOverlay}
              onEditImageWithCanva={onEditImageWithCanva}
              onEditImageWithIA={onEditImageWithIA}
              onEditVideoWithOverlay={onEditVideoWithOverlay}
              onAnimateImageWithOverlay={onAnimateImageWithOverlay}
              showAnimate={!isCarousel}
              showRatingBadge={showRatingBadge}
              currentRating={currentRating}
              t={t}
              showDimensionsOverlay={showDimensionsOverlay}
              dimensionsText={dimensionsText}
              showVideoThumbnailFallback={Boolean(
                item?.isVideoThumbnailFallback
              )}
              videoThumbnailFallbackTooltip={t(
                GALLERY_VIDEO_THUMBNAIL_FALLBACK_TOOLTIP
              )}
              isSelectable={isSelectable}
              isViewOnly={isViewOnly}
              multiple={multiple}
              overlayPickerMode={overlayPickerMode}
              isSelected={isSelected}
              onSelectImageItem={onSelectImageItem}
            />

            <Box
              ref={releaseWhenHidden ? mediaInViewRef : undefined}
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: containerAspectRatio,
                flexShrink: 0,
                overflow: "hidden",
                backgroundColor: isCarousel ? "#ffffff" : "transparent",
                ...(showPngAssetBackdrop ? PNG_ASSET_BACKDROP_SX : {}),
                "&:hover .gallery-video-mute-toggle": {
                  opacity: 1,
                },
                "&:hover .gallery-video-mute-toggle .MuiIconButton-root": {
                  pointerEvents: "auto",
                },
              }}
            >
              {!showMedia ? (
                <Skeleton
                  variant="rounded"
                  sx={{ position: "absolute", inset: 0, height: "100%" }}
                />
              ) : isCarousel ? (
                <SlideTrack
                  urls={carouselSlideUrls}
                  index={currentCarouselIndex}
                  sx={{ position: "absolute", inset: 0 }}
                  renderSlide={(slideUrl, _slideIndex, isActive) => (
                    <CardMedia
                      {...propsMedia}
                      eager
                      releaseWhenHidden={releaseWhenHidden}
                      url={slideUrl}
                      onFailedLoadUrl={isActive ? onFailedLoad : undefined}
                      onMediaLoad={() => markCarouselUrlReady(slideUrl)}
                      sx={{
                        objectFit: "cover",
                        borderRadius: 0,
                        width: "100%",
                        height: "100%",
                        ...(propsMedia.sx || {}),
                      }}
                      sxVideo={{
                        borderRadius: 0,
                        ...(propsMedia.sxVideo || {}),
                      }}
                      propsType={{
                        controls: false,
                        autoplay: isActive,
                        muted: isActive ? isVideoMuted : true,
                        loop: true,
                        ...(propsMedia.propsType || {}),
                      }}
                    />
                  )}
                />
              ) : (
                <CardMedia
                  {...propsMedia}
                  releaseWhenHidden={releaseWhenHidden}
                  url={currentUrl}
                  onFailedLoadUrl={onFailedLoad}
                  sx={{
                    objectFit: "cover",
                    borderRadius: 0,
                    width: "100%",
                    height: "100%",
                    ...(propsMedia.sx || {}),
                  }}
                  sxVideo={{
                    borderRadius: 0,
                    ...(propsMedia.sxVideo || {}),
                  }}
                  propsType={{
                    controls: false,
                    autoplay: true,
                    muted: isVideoMuted,
                    loop: true,
                    ...(propsMedia.propsType || {}),
                  }}
                />
              )}

              {isCurrentCarouselSlideLoading && showMedia ? (
                <CarouselSlideLoader />
              ) : null}

              {isVideo && (
                <VideoMuteToggle
                  isMuted={isVideoMuted}
                  onToggleMute={onToggleVideoMute}
                />
              )}

              <CarouselControls
                isCarousel={isCarousel}
                totalImages={item?.carouselImages?.length || 0}
                currentIndex={currentCarouselIndex}
                isLocked={isSlideLocked}
                onPrev={handleCarouselPrev}
                onNext={handleCarouselNext}
              />

              {showShimmer && isImage && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 20,
                    pointerEvents: "none",
                    background:
                      "linear-gradient(to right, transparent, rgba(255,255,255,0.2), transparent)",
                    transform: "translateX(-150%) rotate(25deg)",
                    "@keyframes shimmerSlide": {
                      "0%": {
                        transform: "translateX(-150%) rotate(25deg)",
                        opacity: 0,
                      },
                      "20%": { opacity: 1 },
                      "80%": { opacity: 1 },
                      "100%": {
                        transform: "translateX(150%) rotate(25deg)",
                        opacity: 0,
                      },
                    },
                    animation: "shimmerSlide 2.5s ease-in-out infinite",
                  }}
                />
              )}
            </Box>

            {showAssetName ? (
              <GalleryAssetNameCaption
                item={item}
                onRenameAsset={onRenameAsset}
              />
            ) : null}
            <ContentPlatformStatuses statuses={platformStatuses} />
            <ActionButtonsOverlay
              showActionButtons={showActionButtons}
              fromCalendar={fromCalendar}
              multiple={multiple}
              overlayPickerMode={overlayPickerMode}
              showDownloadButton={showDownloadButton}
              showDeleteButton={showDeleteButton}
              showPreviewExpandOnly={showPreviewExpandOnly}
              showExpandButton={showExpandButton}
              showFooterEditButton={showFooterEditButton}
              onFooterEdit={onFooterEditClick}
              showFooterDeleteButton={showFooterDeleteButton}
              onFooterDelete={onFooterDeleteClick}
              postLabel={t(POST)}
              isDownloading={isDownloading}
              onDownloadMedia={onDownloadMedia}
              onUploadFromSocial={onUploadFromSocial}
              onSelectPreviewImage={onSelectPreviewImage}
              onDeleteContent={onDeleteOrRemoveContent}
              deleteActionLabel={deleteActionLabel}
              DeleteActionIcon={DeleteActionIcon}
              isRemoveFromFolder={isRemoveFromFolder}
              contentApprovalControls={
                contentApprovalControlsProps ? (
                  <ContentApprovalControls
                    {...contentApprovalControlsProps}
                    overrideRejectedLabel={t(
                      GALLERY_CONTENT_APPROVAL_STATUS_REJECTED
                    )}
                  />
                ) : null
              }
              showFeedbackButtons={showFeedbackButtons}
              liked={liked}
              disliked={disliked}
              likeAnimating={likeAnimating}
              dislikeAnimating={dislikeAnimating}
              onLikeClick={onLike}
              onDislikeClick={onDislike}
              showReportButton={
                showReportButton && !hasSuccessfullyReportedContent
              }
              showPostButton={showPostButton}
              onOpenReportModal={onOpenReportModal}
              actionStripMinHeight={actionStripMinHeight}
            />
          </>
        )}
      </GalleryCardContainer>
    );
  };

  return (
    <>
      {renderContent()}

      {/* Modal de compartir */}
      {!fromCalendar && isShareModalOpen && showPostButton && (
        <ShareModalMultiple
          item={
            isCarousel && item?.carouselImages
              ? {
                  ...item,
                  carouselImages: item.carouselImages,
                  isCarousel: true,
                }
              : item
          }
          modalOpen={isShareModalOpen}
          onCloseModal={() => setIsShareModalOpen(false)}
          scheduledDate={scheduledDate}
          onRefreshCalendar={onRefreshCalendar}
          onScheduledShareSuccess={onScheduledShareSuccess}
          onShareSuccess={onShareSuccess}
        />
      )}

      {isReportModalOpen && (
        <ModalReportContent
          modalOpen={isReportModalOpen}
          onCloseModal={onCloseReportModal}
          onSubmitReport={onSubmitReport}
          isSubmitting={isSubmittingReport}
        />
      )}
    </>
  );
};

// Memoize to avoid unnecessary re-renders; only rerenders on relevant prop changes
// eslint-disable-next-line
export default memo(
  GalleryItem,
  function areGalleryItemPropsEqual(prevProps, nextProps) {
    const prevDimensions = prevProps.itemDimensions?.get(prevProps.item?.url);
    const nextDimensions = nextProps.itemDimensions?.get(nextProps.item?.url);
    const dimensionsChanged = prevDimensions !== nextDimensions;

    return (
      prevProps.item?.url === nextProps.item?.url &&
      prevProps.item?.id === nextProps.item?.id &&
      prevProps.item?.name === nextProps.item?.name &&
      prevProps.item?.publishedSources === nextProps.item?.publishedSources &&
      prevProps.item?.carouselImages === nextProps.item?.carouselImages &&
      prevProps.item?.rating === nextProps.item?.rating &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.item?.resizingFormat === nextProps.item?.resizingFormat &&
      prevProps.item?.resizingWidth === nextProps.item?.resizingWidth &&
      prevProps.item?.resizingHeight === nextProps.item?.resizingHeight &&
      prevProps.multiple === nextProps.multiple &&
      prevProps.index === nextProps.index &&
      prevProps.from === nextProps.from &&
      prevProps.item?.from === nextProps.item?.from &&
      prevProps.item?.isVideoThumbnailFallback ===
        nextProps.item?.isVideoThumbnailFallback &&
      prevProps.showDimensionsOverlay === nextProps.showDimensionsOverlay &&
      prevProps.showActionButtons === nextProps.showActionButtons &&
      prevProps.showResizeFormatButton === nextProps.showResizeFormatButton &&
      prevProps.showEditButton === nextProps.showEditButton &&
      prevProps.showPreviewExpandOnly === nextProps.showPreviewExpandOnly &&
      prevProps.showExpandButton === nextProps.showExpandButton &&
      prevProps.showFooterEditButton === nextProps.showFooterEditButton &&
      prevProps.showFooterDeleteButton === nextProps.showFooterDeleteButton &&
      prevProps.onFooterDelete === nextProps.onFooterDelete &&
      prevProps.item?.libraryID === nextProps.item?.libraryID &&
      prevProps.item?.platform === nextProps.item?.platform &&
      prevProps.item?.pageId === nextProps.item?.pageId &&
      prevProps.item?.preview === nextProps.item?.preview &&
      prevProps.item?.region === nextProps.item?.region &&
      prevProps.showDeleteButton === nextProps.showDeleteButton &&
      prevProps.onRemoveFromFolder === nextProps.onRemoveFromFolder &&
      prevProps.showReportButton === nextProps.showReportButton &&
      prevProps.showPostButton === nextProps.showPostButton &&
      prevProps.overlayPickerMode === nextProps.overlayPickerMode &&
      prevProps.showDownloadButton === nextProps.showDownloadButton &&
      prevProps.listRoot === nextProps.listRoot &&
      prevProps.showRating === nextProps.showRating &&
      prevProps.scheduledDate === nextProps.scheduledDate &&
      prevProps.onRefreshCalendar === nextProps.onRefreshCalendar &&
      prevProps.onScheduledShareSuccess === nextProps.onScheduledShareSuccess &&
      prevProps.onShareSuccess === nextProps.onShareSuccess &&
      prevProps.selectionNumber === nextProps.selectionNumber &&
      prevProps.item?.approvalStatus === nextProps.item?.approvalStatus &&
      prevProps.item?.contentApprovalID === nextProps.item?.contentApprovalID &&
      prevProps.enablingContentApprovalId ===
        nextProps.enablingContentApprovalId &&
      prevProps.showContentApproval === nextProps.showContentApproval &&
      prevProps.showContentApprovalEnableButton ===
        nextProps.showContentApprovalEnableButton &&
      prevProps.showShimmer === nextProps.showShimmer &&
      prevProps.isHighlighted === nextProps.isHighlighted &&
      prevProps.draggableForFolders === nextProps.draggableForFolders &&
      prevProps.itemSelectionId === nextProps.itemSelectionId &&
      prevProps.selectedItemsCount === nextProps.selectedItemsCount &&
      prevProps.showAssetName === nextProps.showAssetName &&
      prevProps.actionStripMinHeight === nextProps.actionStripMinHeight &&
      prevProps.releaseWhenHidden === nextProps.releaseWhenHidden &&
      prevProps.unmutedVideoKey === nextProps.unmutedVideoKey &&
      prevProps.onRequestVideoUnmute === nextProps.onRequestVideoUnmute &&
      prevProps.onRequestVideoMute === nextProps.onRequestVideoMute &&
      !dimensionsChanged
    );
  }
);
